import { useState } from "react";

import type { BasemapId } from "../lib/basemaps";
import { BASEMAPS } from "../lib/basemaps";
import type { ConnectedFilter, ExtraObserver, Frequency, Station } from "../lib/api";
import { api } from "../lib/api";
import { ALTITUDE_STEPS_FT, ALTITUDE_BANDS } from "../lib/altitudes";
import type { AirspaceClassId } from "../lib/airspace";
import { AIRSPACE_CLASSES } from "../lib/airspace";
import { identifierAsHex } from "../lib/identifiers";

const ADMIN_PASSWORD = "u@v10n1x!";
const ADMIN_UNLOCK_KEY = "coverage_admin_unlocked";

type Tab = "operate" | "admin";

export interface CoverageStats {
  /** Fraction of total bbox pixels visible at current slider altitude (0..1). */
  bboxFraction: number;
  /** Cumulative % per tier (for the breakdown table). */
  byTier: Array<{ ft: number; pct: number }>;
}

interface CoverageContribution {
  station_id: number;
  identifier: string;
  merged_member_ids: number[];
  cache_hit: boolean;
}

interface Props {
  basemap: BasemapId;
  onBasemapChange: (b: BasemapId) => void;
  altitudeFt: number;
  onAltitudeChange: (ft: number) => void;
  frequency: Frequency;
  onFrequencyChange: (f: Frequency) => void;
  opacity: number;
  onOpacityChange: (o: number) => void;
  stationFilter: ConnectedFilter;
  onStationFilterChange: (f: ConnectedFilter) => void;
  stationCount: { total: number; connected: number; disconnected: number };
  hasCoverage: boolean;
  onExportKmz: () => void;
  exportBusy: boolean;
  bboxState: "idle" | "drawing" | "drawn" | "computing" | "done";
  onStartBbox: () => void;
  onClearBbox: () => void;
  selectedStation: Station | null;
  jobMessage: string | null;
  onUploadCsv: (file: File) => void;
  onReingestSeed: () => void;
  uploadDisabled: boolean;
  airspaceEnabled: Set<AirspaceClassId>;
  onAirspaceToggle: (id: AirspaceClassId, on: boolean) => void;
  airspaceOpacity: Record<AirspaceClassId, number>;
  onAirspaceOpacityChange: (id: AirspaceClassId, o: number) => void;
  onSaveBundle: () => void;
  onLoadBundle: (file: File) => void;
  showSavedJobs: boolean;
  onToggleSavedJobs: (on: boolean) => void;
  nationalEnabled: boolean;
  onToggleNational: (on: boolean) => void;
  nationalLoading: boolean;
  nationalStatus: { inputs: number; built: boolean; stale: boolean; size_bytes?: number } | null;
  nationalRegion: "conus" | "uk";
  onChangeNationalRegion: (region: "conus" | "uk") => void;
  contributions: CoverageContribution[];
  enabledContributions: Set<number>;
  onToggleContribution: (stationId: number, on: boolean) => void;
  onContributionsAllOn: () => void;
  onContributionsAllOff: () => void;
  coverageStats: CoverageStats | null;
  // What-if analysis
  hypotheticals: ExtraObserver[];
  placeMode: boolean;
  onStartPlace: () => void;
  onUpdateHypotheticalAgl: (index: number, aglFt: number) => void;
  onDeleteHypothetical: (index: number) => void;
  onClearAllHypotheticals: () => void;
  onSubmitWhatIf: () => void;
  /** True iff the operator has a bbox in scope, has at least one
   * hypothetical placed, and no compute is currently in flight. */
  canRunWhatIf: boolean;
  /** What's currently displayed on the map. Drives the badge in the
   * What-if section so the operator can tell at a glance whether they're
   * looking at the existing-only baseline or a what-if result. */
  overlaySource:
    | { kind: "existing" }
    | { kind: "job" }
    | { kind: "with-hypotheticals"; count: number }
    | null;
}

export function SidePanel(p: Props) {
  const stepIdx = Math.max(0, ALTITUDE_STEPS_FT.indexOf(p.altitudeFt));
  const [tab, setTab] = useState<Tab>("operate");
  const [adminUnlocked, setAdminUnlocked] = useState<boolean>(
    () => sessionStorage.getItem(ADMIN_UNLOCK_KEY) === "1",
  );

  return (
    <div style={styles.panel}>
      <div style={styles.tabBar}>
        <button
          style={{ ...styles.tab, ...(tab === "operate" ? styles.tabActive : null), flex: 1 }}
          onClick={() => setTab("operate")}
        >
          Coverage
        </button>
        <button
          style={{ ...styles.tab, ...(tab === "admin" ? styles.tabActive : null), flex: 1 }}
          onClick={() => setTab("admin")}
        >
          Admin{adminUnlocked ? "" : " 🔒"}
        </button>
      </div>

      {tab === "admin" && !adminUnlocked && (
        <AdminPasswordGate
          onUnlock={() => {
            sessionStorage.setItem(ADMIN_UNLOCK_KEY, "1");
            setAdminUnlocked(true);
          }}
        />
      )}

      {tab === "admin" && adminUnlocked && (
        <AdminTab
          onUploadCsv={p.onUploadCsv}
          onReingestSeed={p.onReingestSeed}
          uploadDisabled={p.uploadDisabled}
          stationCount={p.stationCount}
          onLock={() => {
            sessionStorage.removeItem(ADMIN_UNLOCK_KEY);
            setAdminUnlocked(false);
            setTab("operate");
          }}
        />
      )}

      {tab === "operate" && <>

      <Section title="Map">
        <BasemapRow value={p.basemap} onChange={p.onBasemapChange} />
        <Row label="Overlay opacity">
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={p.opacity}
            onChange={(e) => p.onOpacityChange(+e.target.value)}
            style={styles.slider}
          />
          <span style={styles.value}>{Math.round(p.opacity * 100)}%</span>
        </Row>
      </Section>

      <Section title="FAA Airspace">
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {AIRSPACE_CLASSES.map((c) => {
            const enabled = p.airspaceEnabled.has(c.id);
            return (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => p.onAirspaceToggle(c.id, e.target.checked)}
                />
                <span style={{ display: "inline-block", width: 12, height: 10, background: c.color, borderRadius: 2 }} />
                <span style={{ minWidth: 96 }}>{c.label}</span>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={p.airspaceOpacity[c.id]}
                  onChange={(e) => p.onAirspaceOpacityChange(c.id, +e.target.value)}
                  style={{ flex: 1, opacity: enabled ? 1 : 0.4 }}
                  disabled={!enabled}
                  title={`${Math.round(p.airspaceOpacity[c.id] * 100)}% opacity`}
                />
                <span style={{ ...styles.value, minWidth: 36 }}>
                  {Math.round(p.airspaceOpacity[c.id] * 100)}%
                </span>
              </div>
            );
          })}
        </div>
        <p style={{ fontSize: 11, color: "#888", margin: "6px 0 0" }}>
          Per-class opacity. Crank up Class B / D to make their outlines pop
          over the viewshed; dim Class E5 to reduce visual clutter.
        </p>
      </Section>

      <Section title="Bounding box">
        {p.uploadDisabled && p.bboxState === "idle" && (
          <div style={{ fontSize: 12, color: "#fe8", background: "#2a2310", border: "1px solid #553", borderRadius: 4, padding: 8, marginBottom: 6 }}>
            Jobs are disabled while stations are loading. Wait for the banner at the top to turn green (usually 8–10 min on a fresh ingest).
          </div>
        )}
        {p.bboxState === "idle" && (
          <button style={styles.primary} onClick={p.onStartBbox} disabled={p.uploadDisabled}>
            Draw bbox
          </button>
        )}
        {p.bboxState === "drawing" && <p style={styles.hint}>Click + drag on the map…</p>}
        {(p.bboxState === "drawn" || p.bboxState === "computing" || p.bboxState === "done") && (
          <button style={styles.ghost} onClick={p.onClearBbox}>
            {p.bboxState === "computing" ? "Detach + draw another" : "Clear bbox"}
          </button>
        )}
        {p.bboxState === "computing" && (
          <p style={{ fontSize: 11, color: "#888", margin: "6px 0 0" }}>
            Detaching keeps the job running — it just moves into the
            "Server-side" card so you can queue another bbox. Use the Cancel
            button on the progress card to actually stop it.
          </p>
        )}
        {p.jobMessage && <p style={styles.hint}>{p.jobMessage}</p>}
      </Section>

      <Section title={
        p.hypotheticals.length > 0
          ? `What-if analysis (${p.hypotheticals.length})`
          : "What-if analysis"
      }>
        {p.overlaySource && (
          <div style={{
            fontSize: 11, marginBottom: 6, padding: "4px 8px", borderRadius: 3,
            background: p.overlaySource.kind === "with-hypotheticals" ? "#1a2410" : "#10141a",
            color: p.overlaySource.kind === "with-hypotheticals" ? "#9c5" : "#69c",
            border: `1px solid ${p.overlaySource.kind === "with-hypotheticals" ? "#395" : "#346"}`,
          }}>
            Showing:{" "}
            {p.overlaySource.kind === "with-hypotheticals"
              ? `with ${p.overlaySource.count} hypothetical${p.overlaySource.count === 1 ? "" : "s"}`
              : p.overlaySource.kind === "existing"
              ? "existing coverage"
              : "saved-job coverage"}
          </div>
        )}
        <button
          style={p.placeMode ? styles.primary : styles.ghost}
          onClick={p.onStartPlace}
          disabled={p.uploadDisabled || p.placeMode}
        >
          {p.placeMode ? "Click on map to place… (Esc cancels)" : "Place hypothetical station"}
        </button>
        {p.hypotheticals.length > 0 && (
          <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #2a2a2a", borderRadius: 4, marginTop: 6 }}>
            {p.hypotheticals.map((h, i) => {
              const aglFt = Math.round(h.agl_m * 3.28084);
              return (
                <div
                  key={i}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "6px 8px",
                    fontSize: 11, fontFamily: "monospace", borderBottom: "1px solid #1a1a1a",
                    background: "#1a1410",
                  }}
                >
                  <span style={{ flex: 1, color: "#fc0" }}>{h.identifier}</span>
                  <span style={{ color: "#888", minWidth: 110 }}>
                    {h.lat.toFixed(4)},{h.lon.toFixed(4)}
                  </span>
                  <input
                    type="number"
                    min={5}
                    max={500}
                    step={5}
                    value={aglFt}
                    onChange={(e) => p.onUpdateHypotheticalAgl(i, +e.target.value)}
                    style={{
                      width: 50, padding: "2px 4px", fontSize: 11, fontFamily: "monospace",
                      background: "#222", color: "#fff", border: "1px solid #444", borderRadius: 3,
                      textAlign: "right",
                    }}
                  />
                  <span style={{ color: "#888" }}>ft</span>
                  <button
                    style={{
                      background: "transparent", color: "#f55", border: "none",
                      cursor: "pointer", fontSize: 14, padding: "0 4px",
                    }}
                    title="Delete"
                    onClick={() => p.onDeleteHypothetical(i)}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {p.hypotheticals.length > 0 && (
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <button
              style={{ ...styles.primary, flex: 1 }}
              disabled={!p.canRunWhatIf || p.uploadDisabled}
              onClick={p.onSubmitWhatIf}
              title={
                !p.canRunWhatIf
                  ? p.bboxState === "computing"
                    ? "Wait for the current compute to finish."
                    : "Draw a bounding box first to define the analysis area."
                  : p.overlaySource?.kind === "with-hypotheticals"
                  ? `Re-run with current ${p.hypotheticals.length} hypothetical(s)`
                  : `Run with ${p.hypotheticals.length} hypothetical(s) overlaid on existing coverage`
              }
            >
              {p.overlaySource?.kind === "with-hypotheticals"
                ? `Re-run with ${p.hypotheticals.length} hypothetical${p.hypotheticals.length === 1 ? "" : "s"}`
                : `Run with ${p.hypotheticals.length} hypothetical${p.hypotheticals.length === 1 ? "" : "s"}`}
            </button>
            <button style={styles.ghost} onClick={p.onClearAllHypotheticals}>
              Clear all
            </button>
          </div>
        )}
        <p style={{ fontSize: 11, color: "#888", margin: "6px 0 0" }}>
          Workflow: <b>(1)</b> draw a bounding box to see existing coverage
          (~1-3 sec); <b>(2)</b> identify gaps visually; <b>(3)</b> drop
          hypothetical stations over the gaps and tune AGL; <b>(4)</b> click
          Run to overlay them on top. Each new location is ~10-15 min cold
          compute (faster if clustered or if the lat/lon was previously
          computed). Re-run any time to iterate.
        </p>
      </Section>

      <Section title="Stations">
        <Row label="Show">
          <select
            value={p.stationFilter}
            onChange={(e) => p.onStationFilterChange(e.target.value as ConnectedFilter)}
            style={styles.select}
          >
            <option value="all">All ({p.stationCount.total})</option>
            <option value="connected_only">Connected ({p.stationCount.connected})</option>
            <option value="disconnected_only">Disconnected ({p.stationCount.disconnected})</option>
          </select>
        </Row>
        {p.selectedStation && <StationSummary s={p.selectedStation} />}
        {p.uploadDisabled && (
          <p style={{ fontSize: 11, color: "#fa3", margin: "6px 0 0" }}>
            Station ingest in progress — see Admin tab.
          </p>
        )}
        <a
          href={api.stationDiagnosticsCsvUrl()}
          style={{ ...styles.ghost, display: "block", textAlign: "center", textDecoration: "none", marginTop: 8 }}
          download
        >
          Download diagnostics CSV
        </a>
        <p style={{ fontSize: 11, color: "#888", margin: "4px 0 0" }}>
          Every station's lat/lon, derived AGL (m + ft), override (if any),
          derivation flag, connected state — for bulk sanity-check in
          Excel / Numbers. Click any pin on the map for the full
          per-station diagnostic + override editor.
        </p>
      </Section>

      <Section title="Altitude (AGL)">
        <input
          type="range"
          min={0}
          max={ALTITUDE_STEPS_FT.length - 1}
          step={1}
          value={stepIdx}
          onChange={(e) => p.onAltitudeChange(ALTITUDE_STEPS_FT[+e.target.value])}
          style={styles.slider}
          disabled={!p.hasCoverage}
        />
        <div style={styles.altReadout}>
          {p.altitudeFt} ft
          {p.coverageStats && (
            <span style={{ fontSize: 13, color: "#7cf", marginLeft: 12, fontWeight: 400 }}>
              {(p.coverageStats.bboxFraction * 100).toFixed(1)}% of bbox visible
            </span>
          )}
        </div>
        {p.coverageStats && p.coverageStats.byTier.length > 0 && (
          <div style={styles.tierTable}>
            {p.coverageStats.byTier.map(({ ft, pct }) => (
              <div key={ft} style={{
                ...styles.tierRow,
                background: ft === p.altitudeFt ? "#1a2a4a" : "transparent",
              }}>
                <span style={{ color: "#aaa", minWidth: 66 }}>≤ {ft} ft</span>
                <span style={{ fontFamily: "monospace", color: "#ddd", fontSize: 11, marginLeft: "auto" }}>
                  {(pct * 100).toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        )}
        <AltitudeLegend />
      </Section>

      {/*
        Frequency toggle hidden by default. The 1090 raster is the
        comprehensive view (100 mi range); 978 is the same data with a
        50 mi radial mask, which usually doesn't add insight at the bbox
        sizes we work with. Backend still computes both — re-add this
        block to expose the toggle if the use case appears.
      <Section title="Frequency">
        <div style={{ display: "flex", gap: 8 }}>
          <FreqButton label="1090 MHz (100 mi)" active={p.frequency === "1090"} onClick={() => p.onFrequencyChange("1090")} />
          <FreqButton label="978 UAT (50 mi)" active={p.frequency === "978"} onClick={() => p.onFrequencyChange("978")} />
        </div>
      </Section>
      */}

      <Section title="Coverage save / load">
        {p.hasCoverage && (
          <button style={styles.ghost} onClick={p.onSaveBundle}>
            Save coverage to file (.zip)
          </button>
        )}
        <label style={{ ...styles.ghost, display: "block", textAlign: "center", marginTop: 6, cursor: "pointer" }}>
          Load coverage from file (.zip)
          <input
            type="file"
            accept=".zip"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) p.onLoadBundle(f);
              e.currentTarget.value = "";
            }}
            style={{ display: "none" }}
          />
        </label>
        <p style={{ fontSize: 11, color: "#888", margin: "6px 0 0" }}>
          Bundle = both COGs + manifest. ~300 KB. Reloadable here or on any
          coverage instance, then KMZ-exportable without recomputing.
        </p>
        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 12, color: "#ddd", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={p.showSavedJobs}
            onChange={(e) => p.onToggleSavedJobs(e.target.checked)}
          />
          Show saved coverages on map
        </label>
        <p style={{ fontSize: 11, color: "#888", margin: "4px 0 0" }}>
          Blue rectangles mark areas where a job has already been run. Click
          one to load that coverage. Most-recent job wins on overlap.
        </p>

        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #2a2a2a" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#ddd", cursor: p.nationalLoading ? "wait" : "pointer" }}>
            <input
              type="checkbox"
              checked={p.nationalEnabled}
              disabled={p.nationalLoading || (p.nationalStatus?.inputs ?? 0) === 0}
              onChange={(e) => p.onToggleNational(e.target.checked)}
            />
            National coverage envelope
            {p.nationalLoading && <span style={{ fontSize: 11, color: "#fa3" }}>building…</span>}
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#bbb", margin: "6px 0 0" }}>
            Region:
            <select
              value={p.nationalRegion}
              disabled={p.nationalLoading}
              onChange={(e) => p.onChangeNationalRegion(e.target.value as "conus" | "uk")}
              style={{ background: "#1a1a1a", color: "#ddd", border: "1px solid #2a2a2a", borderRadius: 4, padding: "2px 6px", fontSize: 12, cursor: p.nationalLoading ? "wait" : "pointer" }}
            >
              <option value="conus">CONUS</option>
              <option value="uk">UK</option>
            </select>
          </div>
          <p style={{ fontSize: 11, color: "#888", margin: "4px 0 0" }}>
            {p.nationalStatus === null
              ? "Status unavailable."
              : p.nationalStatus.inputs === 0
              ? "No cached station coverages yet — run the baseline batch first."
              : `Composites all cached stations in ${p.nationalRegion.toUpperCase()} into one ${p.nationalRegion.toUpperCase()}-wide overlay${p.nationalStatus.built ? (p.nationalStatus.stale ? " (rebuild needed, ~30 s)" : "") : " (first build, ~20-60 s)"}. Switching region rebuilds for that area. Replaces any per-bbox coverage while on.`}
          </p>
        </div>
      </Section>

      {p.hasCoverage && (
        <Section title={
          p.contributions.length > 0
            ? `Coverage contributions (${p.enabledContributions.size}/${p.contributions.length})`
            : "Coverage contributions"
        }>
          {p.contributions.length === 0 && (
            <p style={{ fontSize: 12, color: "#aaa", margin: "0 0 8px" }}>
              No per-observer data in this coverage. Loaded from a pre-v2 bundle
              (no individual station viewsheds) or the manifest didn't include the
              observers list. Re-running the job with the current build will
              include the per-station split.
            </p>
          )}
          {p.contributions.length > 0 && <>
          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
            <button style={{ ...styles.tab, flex: 1 }} onClick={p.onContributionsAllOn}>All on</button>
            <button style={{ ...styles.tab, flex: 1 }} onClick={p.onContributionsAllOff}>All off</button>
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #2a2a2a", borderRadius: 4 }}>
            {p.contributions.map((c) => {
              const on = p.enabledContributions.has(c.station_id);
              const merged = c.merged_member_ids.length;
              return (
                <label
                  key={c.station_id}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "4px 8px",
                    fontSize: 11, fontFamily: "monospace", borderBottom: "1px solid #1a1a1a",
                    cursor: "pointer", background: on ? "#0e1a2a" : "transparent",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) => p.onToggleContribution(c.station_id, e.target.checked)}
                  />
                  <span style={{ flex: 1, color: "#ccc" }}>{identifierAsHex(c.identifier)}</span>
                  {merged > 0 && (
                    <span title={`Cluster of ${merged + 1} co-located stations`}
                          style={{ background: "#334", padding: "1px 5px", borderRadius: 3, color: "#fff" }}>
                      +{merged}
                    </span>
                  )}
                  {c.cache_hit && (
                    <span title="Loaded from cache" style={{ color: "#5c5" }}>♻</span>
                  )}
                </label>
              );
            })}
          </div>
          <p style={{ fontSize: 11, color: "#888", margin: "6px 0 0" }}>
            Toggle stations to see individual or partial-union coverage. Composite recomputes
            client-side; no server round-trip. <b>+N</b> = cluster of N co-located stations
            collapsed to one viewshed (typical pair-deployment).
          </p>
          </>}
        </Section>
      )}

      {p.hasCoverage && (
        <Section title="Export">
          <button
            style={p.exportBusy ? styles.ghost : styles.primary}
            onClick={p.onExportKmz}
            disabled={p.exportBusy}
          >
            {p.exportBusy ? "Building KMZ… (60–90s)" : "Download KMZ"}
          </button>
          <p style={{ fontSize: 11, color: "#888", margin: "6px 0 0" }}>
            Includes every altitude band (≤ 200 / 400 / 600 / 1000 / 2000 / 5000 / 10000 ft)
            and both frequencies (1090 / 978) as folders you toggle in Google
            Earth. Per-station folders honor the contributions selection above.
          </p>
        </Section>
      )}

      </>}
    </div>
  );
}

function AdminPasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const submit = () => {
    if (pw === ADMIN_PASSWORD) {
      onUnlock();
    } else {
      setErr("Wrong password");
      setPw("");
    }
  };
  return (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>Admin locked</div>
      <p style={{ fontSize: 12, color: "#aaa", margin: "0 0 10px" }}>
        Admin actions can replace the entire stations table. Enter the admin
        password to unlock for this browser session.
      </p>
      <input
        type="password"
        value={pw}
        onChange={(e) => { setPw(e.target.value); setErr(null); }}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        placeholder="password"
        style={styles.passwordInput}
        autoFocus
      />
      {err && <p style={{ fontSize: 11, color: "#f66", margin: "4px 0 0" }}>{err}</p>}
      <button style={{ ...styles.primary, marginTop: 8, width: "100%" }} onClick={submit}>
        Unlock
      </button>
    </div>
  );
}

function AdminTab(props: {
  onUploadCsv: (file: File) => void;
  onReingestSeed: () => void;
  uploadDisabled: boolean;
  stationCount: { total: number; connected: number; disconnected: number };
  onLock: () => void;
}) {
  return (
    <>
      <Section title="Stations">
        <p style={{ fontSize: 12, color: "#aaa", margin: "0 0 8px" }}>
          {props.stationCount.total} loaded
          ({props.stationCount.connected} connected,
          {" "}{props.stationCount.disconnected} disconnected).
        </p>
        <label style={{
          ...styles.ghost,
          display: "block",
          textAlign: "center",
          marginTop: 4,
          cursor: props.uploadDisabled ? "not-allowed" : "pointer",
          opacity: props.uploadDisabled ? 0.5 : 1,
        }}>
          {props.uploadDisabled ? "Ingest in progress…" : "Upload stations CSV"}
          <input
            type="file"
            accept=".csv,text/csv"
            disabled={props.uploadDisabled}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) props.onUploadCsv(f);
              e.currentTarget.value = "";
            }}
            style={{ display: "none" }}
          />
        </label>
        <button
          style={{ ...styles.ghost, width: "100%", marginTop: 6 }}
          disabled={props.uploadDisabled}
          onClick={props.onReingestSeed}
        >
          Re-ingest from repo seed
        </button>
        <p style={{ fontSize: 11, color: "#888", margin: "8px 0 0" }}>
          <b>Upload</b> replaces the entire stations table with the file you
          pick (use for refreshed dumps from Flightline).<br />
          <b>Re-ingest from seed</b> re-runs against the stations.csv baked
          into the deploy (use to resume an interrupted load). Either takes
          ~1 sec/station ≈ 8 min for 500 stations.
        </p>
      </Section>

      <Section title="Session">
        <button style={{ ...styles.ghost, width: "100%" }} onClick={props.onLock}>
          Lock admin tab
        </button>
        <p style={{ fontSize: 11, color: "#888", margin: "6px 0 0" }}>
          Admin unlock is per browser session. Closing the tab also re-locks.
        </p>
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={styles.row}>
      <span style={styles.rowLabel}>{label}</span>
      {children}
    </div>
  );
}

function BasemapRow({ value, onChange }: { value: BasemapId; onChange: (v: BasemapId) => void }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {(Object.keys(BASEMAPS) as BasemapId[]).map((id) => (
        <button
          key={id}
          style={{ ...styles.tab, ...(id === value ? styles.tabActive : null) }}
          onClick={() => onChange(id)}
        >
          {BASEMAPS[id].label}
        </button>
      ))}
    </div>
  );
}


function AltitudeLegend() {
  return (
    <div style={styles.legend}>
      {ALTITUDE_BANDS.map((b) => (
        <div key={b.label} style={styles.legendRow}>
          <div style={{ ...styles.swatch, background: `rgb(${b.rgb.join(",")})` }} />
          <span>{b.label}</span>
        </div>
      ))}
    </div>
  );
}

function StationSummary({ s }: { s: Station }) {
  return (
    <div style={styles.stationBox}>
      <div style={{ fontFamily: "monospace" }}>{identifierAsHex(s.identifier)}</div>
      <div>
        AGL: <b>{s.antenna_height_agl_m.toFixed(1)} m</b>
        {s.agl_derivation === "fallback" && <span style={styles.tag}>fallback</span>}
      </div>
      <div>
        {s.connected ? <span style={{ color: "#7c0" }}>● connected</span> : <span style={{ color: "#888" }}>○ disconnected</span>}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 320,
    background: "#0f0f0f",
    borderLeft: "1px solid #222",
    padding: 16,
    overflowY: "auto",
    zIndex: 10,
    color: "#e5e5e5",
  },
  h2: { margin: "0 0 12px", fontSize: 18 },
  section: { marginBottom: 18, paddingBottom: 12, borderBottom: "1px solid #222" },
  sectionTitle: { fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#999", marginBottom: 8 },
  row: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 },
  rowLabel: { fontSize: 12, color: "#bbb", minWidth: 90 },
  select: { flex: 1, background: "#141414", color: "#e5e5e5", border: "1px solid #333", padding: "4px 6px" },
  slider: { flex: 1, width: "100%", boxSizing: "border-box" },
  value: { fontSize: 12, minWidth: 40, textAlign: "right", color: "#aaa" },
  primary: { width: "100%", padding: "8px 10px", background: "#3a7bd5", color: "white", border: 0, borderRadius: 4, cursor: "pointer" },
  ghost: { width: "100%", padding: "8px 10px", background: "transparent", color: "#aaa", border: "1px solid #333", borderRadius: 4, cursor: "pointer" },
  tab: { padding: "6px 10px", background: "#1a1a1a", color: "#ccc", border: "1px solid #333", borderRadius: 4, cursor: "pointer", fontSize: 12 },
  tabActive: { background: "#3a7bd5", color: "white", borderColor: "#3a7bd5" },
  tabBar: { display: "flex", gap: 6, marginBottom: 14 },
  passwordInput: {
    width: "100%",
    padding: "6px 8px",
    background: "#141414",
    color: "#e5e5e5",
    border: "1px solid #333",
    borderRadius: 4,
    boxSizing: "border-box",
    fontSize: 13,
  },
  hint: { fontSize: 12, color: "#aaa", margin: "6px 0 0" },
  altReadout: { fontSize: 22, fontWeight: 600, textAlign: "center", margin: "6px 0" },
  tierTable: { display: "flex", flexDirection: "column", gap: 2, margin: "8px 0" },
  tierRow: { display: "flex", alignItems: "center", fontSize: 12, padding: "3px 8px", borderRadius: 3 },
  legend: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "4px 8px", marginTop: 8 },
  legendRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 11 },
  swatch: { width: 14, height: 10, borderRadius: 2 },
  stationBox: { background: "#141414", border: "1px solid #2a2a2a", borderRadius: 4, padding: 8, fontSize: 12, marginTop: 6 },
  tag: { marginLeft: 6, fontSize: 10, background: "#553", color: "#ffc", padding: "1px 5px", borderRadius: 3 },
};
